import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Search, Lock, Star } from 'lucide-react';
import { useTraderInstruments } from '@/api/instruments/use-instruments';
import { useAllPrices } from '@/hooks/useAllPrices';
import { useFavorites } from '@/hooks/useFavorites';
import PriceChangeDisplay from '@/components/mobile/PriceChangeDisplay';
import PriceDisplayWithFlash from '@/components/shared/PriceDisplayWithFlash';
import MobileFloatingNav from '@/components/mobile/MobileFloatingNav';
import { Loader2 } from 'lucide-react';

const MobileMarketPage: React.FC = () => {
    const { t } = useTranslation('mobile');
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState('All');
    const [searchQuery, setSearchQuery] = useState('');
    const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');
    const { isFavorite, toggleFavorite } = useFavorites();

    // Debounce search input before sending it to the backend
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearchQuery(searchQuery.trim());
        }, 300);

        return () => clearTimeout(timer);
    }, [searchQuery]);

    const instrumentTypes = [
        { key: 'All', label: t('all') },
        { key: 'Forex', label: t('forex') },
        { key: 'Crypto', label: t('crypto') },
        { key: 'Stock', label: t('stock') },
        { key: 'Commodity', label: t('commodity') },
    ];

    const {
        data: instrumentsData,
        isLoading,
        isFetching,
    } = useTraderInstruments({
        search: debouncedSearchQuery || undefined,
        type: activeTab !== 'All' ? activeTab.toLowerCase() : undefined,
        per_page: 100,
        page: 1,
    });

    // Get all prices (useAllPrices now fetches all instruments automatically)
    const { prices: allPrices } = useAllPrices(undefined, 1000); // Poll every 1 second for real-time updates

    if (isLoading) {
        return (
            <div className="min-h-screen text-foreground flex items-center justify-center pb-20">
                <Loader2 className="w-8 h-8 animate-spin text-primary" />
            </div>
        );
    }

    // Instrument filtering (type + search) is done server-side via useTraderInstruments
    const filteredInstruments = instrumentsData?.data || [];

    const handlePairClick = (instrument: any) => {
        navigate(`/trade/${instrument.id}`, {
            state: { instrument },
        });
    };

    return (
        <div className="min-h-screen text-foreground pb-32">
            {/* Header */}
            <div className="px-4 pt-6 pb-4">
                <div className="flex items-center gap-2 mb-6 trader-glass rounded-2xl px-4 py-3">
                    <Search className="w-5 h-5 text-muted-foreground" />
                    <input
                        type="text"
                        placeholder={t('searchInstruments')}
                        value={searchQuery}
                        onChange={e => setSearchQuery(e.target.value)}
                        className="bg-transparent flex-1 outline-none text-foreground placeholder:text-muted-foreground rounded px-2 py-1"
                    />
                    {isFetching && (
                        <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
                    )}
                </div>

                {/* Trade Header */}
                <div className="flex items-center justify-between mb-6">
                    <h1 className="text-2xl font-bold">{t('trade')}</h1>
                    <Lock className="w-6 h-6 text-muted-foreground" />
                </div>

                {/* Instrument Type Tabs */}
                <div className="flex gap-2 mb-4 overflow-x-auto scrollbar-hide">
                    {instrumentTypes.map(({ key, label }) => (
                        <button
                            key={key}
                            onClick={() => setActiveTab(key)}
                            className={`px-4 py-2 rounded-xl whitespace-nowrap transition-colors ${
                                activeTab === key
                                    ? 'trader-fab-gradient text-white'
                                    : 'trader-glass text-muted-foreground'
                            }`}
                        >
                            {label}
                        </button>
                    ))}
                </div>
            </div>

            {/* Trading Pairs List */}
            <div className="px-4 space-y-1">
                {filteredInstruments.map(instrument => {
                    const priceData = allPrices?.get(instrument.id);
                    const bidPrice = priceData?.bid || 0;
                    const askPrice = priceData?.ask || 0;

                    const favorite = isFavorite(instrument.id);

                    return (
                        <div
                            key={instrument.id}
                            className="flex items-center justify-between py-4 border-b border-border hover:bg-foreground/5 transition-colors"
                        >
                            <div
                                onClick={() => handlePairClick(instrument)}
                                className="flex items-center gap-3 flex-1 cursor-pointer"
                            >
                                <button
                                    onClick={e => {
                                        e.stopPropagation();
                                        toggleFavorite(instrument.id);
                                    }}
                                    className="p-1 hover:bg-foreground/10 rounded transition-colors"
                                >
                                    <Star
                                        className={`w-4 h-4 transition-colors ${
                                            favorite
                                                ? 'fill-yellow-500 text-yellow-500'
                                                : 'text-muted-foreground'
                                        }`}
                                    />
                                </button>
                                <div>
                                    <div className="font-semibold">{instrument.symbol}</div>
                                </div>
                            </div>
                            <div
                                onClick={() => handlePairClick(instrument)}
                                className="text-right cursor-pointer"
                            >
                                <div className="space-y-1">
                                    <div className="flex items-center gap-2 justify-end">
                                        <span className="text-xs text-muted-foreground">
                                            {t('buy')}
                                        </span>
                                        <PriceDisplayWithFlash price={askPrice} />
                                    </div>
                                    <div className="flex items-center gap-2 justify-end">
                                        <span className="text-xs text-muted-foreground">
                                            {t('sell')}
                                        </span>
                                        <PriceDisplayWithFlash price={bidPrice} />
                                    </div>
                                </div>
                                {bidPrice > 0 || askPrice > 0 ? (
                                    <PriceChangeDisplay
                                        instrumentId={instrument.id}
                                        currentPrice={bidPrice || askPrice}
                                        className="text-sm mt-1"
                                    />
                                ) : (
                                    <div className="text-sm text-muted-foreground mt-1">--</div>
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>

            {filteredInstruments.length === 0 && (
                <div className="flex items-center justify-center py-12">
                    <div className="text-center text-muted-foreground">
                        <p>{t('noInstrumentsFound')}</p>
                    </div>
                </div>
            )}

            <MobileFloatingNav />
        </div>
    );
};

export default MobileMarketPage;
